import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Sparkles, Database, ShieldCheck, Cloud, Code2, BarChart3, ServerCog, Palette, Workflow, Landmark, LayoutTemplate } from "lucide-react";
import ParticleField from "@/components/ParticleField";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import { lineReveal, fadeUp, staggerParent, SERVICES, STATS, IMAGES, TECH_GALLERY, AI_HIGHLIGHTS, MORE_SERVICES } from "@/lib/site";

const HERO_LINES = ["We engineer", "the intelligent", "loops behind", "modern business."];

const ICONS = { ai: Sparkles, crm: Database, security: ShieldCheck };
const MORE_ICONS = { Cloud, Code2, BarChart3, ServerCog, Palette, Workflow, Landmark, LayoutTemplate };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <div className="absolute inset-0 grid-lines" aria-hidden="true" />
      <motion.div style={{ opacity }} className="absolute inset-0">
        <ParticleField />
      </motion.div>
      {/* radial glow */}
      <div
        className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full opacity-40 blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(0,240,255,0.28), transparent 70%)" }}
        aria-hidden="true"
      />

      <motion.div
        style={{ y }}
        className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-6 pt-28 lg:px-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-cyber" aria-hidden="true" />
          <span className="overline text-cyber">Core Loop Tech · Hong Kong</span>
        </motion.div>

        <h1 data-testid="hero-heading" className="max-w-5xl font-display text-[13vw] font-semibold leading-[0.92] tracking-tighter text-white sm:text-7xl lg:text-8xl">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden py-[0.05em]">
              <motion.span
                className="block"
                variants={lineReveal}
                custom={i}
                initial="hidden"
                animate="show"
              >
                {i === 3 ? (
                  <>
                    modern <span className="text-cyber text-glow">business.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex max-w-3xl flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md font-body text-base leading-relaxed text-white/55">
            A studio building applied AI, custom CRM platforms and hardened network
            security for companies that refuse to stand still.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-cyber px-6 py-3.5 font-body text-sm font-medium text-ink-950 transition-colors duration-300 hover:bg-white"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 font-body text-sm text-white/70 transition-colors duration-300 hover:text-cyber"
            >
              View services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">Scroll</span>
        <span className="h-10 w-px animate-pulse-glow bg-gradient-to-b from-cyber to-transparent" />
      </motion.div>
    </section>
  );
}

function MarqueeBand() {
  const words = ["Applied AI", "CRM Engineering", "Network Security", "Data Systems", "Automation"];
  return (
    <section className="relative border-y border-white/10 bg-ink-900 py-10" aria-hidden="true">
      <Marquee speed={38} gradient={false} autoFill>
        {words.map((w, i) => (
          <span key={i} className="mx-8 flex items-center gap-8">
            <span className="outline-text font-display text-6xl font-bold tracking-tighter sm:text-7xl lg:text-8xl">
              {w}
            </span>
            <span className="text-4xl text-cyber/70">✦</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section data-testid="home-services" className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40">
      <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <span className="overline text-cyber">What we do</span>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-none tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Three loops. One relentless standard.
          </h2>
        </Reveal>
        <Reveal i={1}>
          <Link to="/services" className="group inline-flex items-center gap-2 font-body text-sm text-white/60 transition-colors duration-300 hover:text-cyber">
            All services
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>

      <motion.div
        variants={staggerParent}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="grid gap-5 md:grid-cols-6"
      >
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.key];
          const wide = i === 0;
          return (
            <motion.div
              key={s.key}
              variants={fadeUp}
              custom={i}
              className={wide ? "md:col-span-4" : "md:col-span-2"}
            >
              <TiltCard
                testid={`service-card-${s.key}`}
                className="glossy gloss-top group h-full overflow-hidden rounded-3xl p-8 transition-shadow duration-500 hover:glossy-cyan"
              >
                <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true">
                  <img src={s.image} alt="" className="h-full w-full object-cover opacity-20" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-850 via-ink-850/80 to-transparent" />
                </div>
                <div className="relative [transform:translateZ(40px)]">
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyber/30 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-sm text-white/30">{s.index}</span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 max-w-md font-body text-sm leading-relaxed text-white/50">{s.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cyber/80">
                    {s.tagline}
                  </span>
                </div>
              </TiltCard>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-white/10 bg-ink-900">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} i={i} className="px-6 py-14 lg:px-10">
            <div className="font-display text-4xl font-bold tracking-tighter text-cyber sm:text-5xl">{s.value}</div>
            <div className="mt-2 font-body text-sm text-white/50">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FeatureSplit() {
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <TiltCard max={7} className="relative">
            <div className="clip-frame glossy overflow-hidden rounded-3xl">
              <img src={IMAGES.ai} alt="Abstract AI network visualization" className="h-[440px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/40 to-transparent" aria-hidden="true" />
            </div>
            <div className="absolute -bottom-6 -right-4 glass glow-ring rounded-xl px-6 py-4 [transform:translateZ(60px)]">
              <div className="font-mono text-xs uppercase tracking-widest text-cyber">Loop status</div>
              <div className="mt-1 font-display text-xl font-semibold text-white">Optimising · 24/7</div>
            </div>
          </TiltCard>
        </Reveal>
        <div>
          <Reveal>
            <span className="overline text-cyber">The Core Loop method</span>
          </Reveal>
          <Reveal i={1}>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tighter text-white sm:text-5xl">
              Technology that compounds, not complicates.
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-white/55">
              We don&apos;t ship one-off projects and disappear. Every system we build is
              designed to learn, integrate and improve — a living loop that gets
              sharper with every cycle of data and feedback.
            </p>
          </Reveal>
          <Reveal i={3} className="mt-10">
            <Link
              to="/about"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-body text-sm text-white transition-colors duration-300 hover:border-cyber hover:text-cyber"
            >
              Our philosophy
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <MarqueeBand />
      <ServicesPreview />
      <AISpotlight />
      <TechGallery />
      <MoreServices />
      <StatsBand />
      <FeatureSplit />
    </div>
  );
}

function AISpotlight() {
  return (
    <section data-testid="home-ai" className="relative overflow-hidden border-y border-white/10 bg-ink-900 py-28 lg:py-40">
      <div
        className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full opacity-30 blur-[130px]"
        style={{ background: "radial-gradient(circle, rgba(0,240,255,0.3), transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-2xl">
          <Reveal>
            <span className="overline text-cyber">Artificial intelligence</span>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-none tracking-tighter text-white sm:text-5xl lg:text-6xl">
              AI that ships to production — and keeps learning.
            </h2>
          </Reveal>
          <Reveal i={1}>
            <p className="mt-6 font-body text-base leading-relaxed text-white/55">
              We build the full AI stack: generative copilots, autonomous agents,
              retrieval systems, vision pipelines and predictive models — all wired
              into your data with the guardrails and monitoring enterprises demand.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {AI_HIGHLIGHTS.map((a, i) => (
            <Reveal key={a.title} i={i}>
              <TiltCard
                max={10}
                testid={`ai-highlight-${i}`}
                className="glossy gloss-top group h-full overflow-hidden rounded-3xl transition-shadow duration-500 hover:glossy-cyan"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={a.img} alt={a.title} className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-850 to-transparent" aria-hidden="true" />
                </div>
                <div className="relative p-6 [transform:translateZ(30px)]">
                  <h3 className="font-display text-lg font-semibold tracking-tight text-white">{a.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{a.body}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechGallery() {
  return (
    <section data-testid="home-gallery" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
      <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <span className="overline text-cyber">Systems in motion</span>
          <h2 className="mt-5 max-w-xl font-display text-4xl font-semibold leading-none tracking-tighter text-white sm:text-5xl">
            Where our engineering lives.
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {TECH_GALLERY.map((g, i) => (
          <Reveal key={g.label} i={i % 3}>
            <div className={`group relative overflow-hidden rounded-2xl glossy ${i === 0 || i === 5 ? "md:row-span-2 md:h-full" : ""}`}>
              <img src={g.img} alt={g.label} className="h-56 w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105 md:h-full md:min-h-[224px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/85 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 flex items-center gap-3 p-5">
                <span className="rounded-full border border-cyber/40 bg-ink-950/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyber backdrop-blur">
                  {g.tag}
                </span>
                <span className="font-display text-sm font-medium text-white">{g.label}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function MoreServices() {
  return (
    <section data-testid="home-more-services" className="border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-36">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <span className="overline text-cyber">Full-stack capability</span>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-none tracking-tighter text-white sm:text-5xl">
              Everything a modern tech partner should be.
            </h2>
          </Reveal>
          <Reveal i={1}>
            <Link to="/services" className="group inline-flex items-center gap-2 font-body text-sm text-white/60 transition-colors duration-300 hover:text-cyber">
              Explore all services
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MORE_SERVICES.map((m, i) => {
            const Icon = MORE_ICONS[m.icon] || Workflow;
            return (
              <Reveal key={m.key} i={i % 3}>
                <TiltCard
                  max={8}
                  testid={`more-service-${m.key}`}
                  className="glossy gloss-top group h-full rounded-3xl p-7 transition-shadow duration-500 hover:glossy-cyan"
                >
                  <div className="relative [transform:translateZ(30px)]">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyber/25 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-white">{m.title}</h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{m.body}</p>
                  </div>
                </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
